import type { DashboardStatistics, WasteTypeWithMaterials } from './dashboard.type';
import type { HseSummary } from './hse-summary.type';
import type { ScopeEmissionsSummary } from './utility-emission.type';

/** Path segment for `/dashboard/esg/{persona}/...` */
export type EsgDashboardPersona = 'waste-outlet' | 'recycler' | 'trucker';

export interface CarbonAvoidedByStream {
  /** Material name, falling back to the category name, then 'unknown'. */
  material_label: string;
  total_avoided_tco2e: number;
  emission_factor_versions: string[];
}

export interface CarbonAvoidedMaterialLine {
  material_type: string;
  /** Material name, falling back to the category name, then 'unknown'. */
  material_label: string;
  avoided_tco2e: number;
  emission_factor_version_label: string;
}

/** Why calculations did not complete, tallied — so a zero total can explain itself. */
export interface CarbonCalculationWarningCount {
  warning_code: string;
  count: number;
}

export interface CarbonAvoidedByMaterial {
  /**
   * The catalog category the emission factor itself resolves through — the same
   * dimension `avoided_tco2e_by_stream` uses, so the two breakdowns reconcile.
   * Replaced the free-text `waste_type`, which disagreed with the material on most
   * rows and collapsed Wood/Sludge/Cardboard-and-Paper into "other".
   */
  category_label: string;
  total_avoided_tco2e: number;
  materials: CarbonAvoidedMaterialLine[];
}

/** Carbon scalars on monthly / overview dashboard responses. */
export interface CarbonDashboardScalars {
  total_avoided_tco2e: number;
  total_energy_saved_kwh: number;
  avoided_tco2e_by_stream: CarbonAvoidedByStream[];
  avoided_tco2e_by_material: CarbonAvoidedByMaterial[];
  calculation_run_count: number;
  calculation_skipped_count: number;
  calculation_failed_count: number;
  calculation_pending_count: number;
  /** Completed pickups carrying no completed run — tonnage absent from the total. */
  calculation_missing_count: number;
  calculation_warnings: CarbonCalculationWarningCount[];
}

/** Carbon scalars on per-branch rows (no material breakdown per API). */
export interface CarbonBranchScalars {
  total_avoided_tco2e: number;
  total_energy_saved_kwh: number;
  avoided_tco2e_by_stream: CarbonAvoidedByStream[];
  calculation_run_count: number;
  calculation_skipped_count: number;
  calculation_failed_count: number;
  calculation_pending_count: number;
  /** Completed pickups carrying no completed run — tonnage absent from the total. */
  calculation_missing_count: number;
  calculation_warnings: CarbonCalculationWarningCount[];
}

/** Eight ESG utility scalars (same names as `collection_site_statistics` columns). */
export interface EsgUtilityScalars {
  water_consumption: number;
  water_amount_sar: number;
  electricity_consumption_kwh: number;
  electricity_amount_sar: number;
  oil_consumption_liters: number;
  oil_amount_sar: number;
  gas_consumption_m3: number;
  gas_amount_sar: number;
}

/** Prior calendar month ESG utilities + fill + training (monthly / overview / by-branch root). */
export type EsgPriorMonthScalars = {
  [K in keyof EsgUtilityScalars as `previous_month_${K}`]: number;
} & {
  previous_month_fill_level_report_count: number;
  previous_month_training_hours_total: number | null;
};

/** Per-branch prior month: utilities + fill only (no `previous_month_training_hours_total` on rows). */
export type EsgBranchPriorScalars = {
  [K in keyof EsgUtilityScalars as `previous_month_${K}`]: number;
} & {
  previous_month_fill_level_report_count: number;
};

/** ESG-only fields merged on monthly response (see ESG_DASHBOARD_API.md — flat shape). */
export type EsgMonthlyMergedFields = EsgUtilityScalars & {
  persona: EsgDashboardPersona;
  organization_id: string;
  month_date: string;
  total_waste_diverted_kg: number;
  waste_diversion_rate_percent: number;
  total_waste_recycled_kg: number;
  recycled_waste_breakdown_by_type: WasteTypeWithMaterials[];
  fill_level_report_count: number;
  training_hours_total: number | null;
  scope_emissions: ScopeEmissionsSummary;
  hse?: HseSummary;
  previous_month_hse?: HseSummary;
} & EsgPriorMonthScalars &
  CarbonDashboardScalars;

/** Monthly ESG = full operational dashboard + ESG fields at the same JSON level. */
export type EsgDashboardMonthlyResponse = DashboardStatistics & EsgMonthlyMergedFields;

/** Overview ESG = operational overview + all-time ESG + prior-month ESG snapshot. */
export type EsgOverviewMergedFields = EsgUtilityScalars & {
  persona: EsgDashboardPersona;
  organization_id: string;
  esg_previous_month_start: string | null;
  total_waste_diverted_kg: number;
  waste_diversion_rate_percent: number;
  total_waste_recycled_kg: number;
  recycled_waste_breakdown_by_type: WasteTypeWithMaterials[];
  fill_level_report_count: number;
  training_hours_total: number | null;
  scope_emissions: ScopeEmissionsSummary;
  hse?: HseSummary;
} & EsgPriorMonthScalars &
  CarbonDashboardScalars;

export type EsgDashboardOverviewResponse = DashboardStatistics & EsgOverviewMergedFields;

/** One collection site row (flat utility + prior-month utilities + waste fields). */
export type EsgBranchMonthlyRow = EsgUtilityScalars &
  EsgBranchPriorScalars & {
    collection_site_id: string;
    collection_site_name: string | null;
    total_waste_diverted_kg: number;
    waste_diversion_rate_percent: number;
    total_waste_recycled_kg: number;
    recycled_waste_breakdown_by_type: WasteTypeWithMaterials[];
    fill_level_report_count: number;
    scope_emissions: ScopeEmissionsSummary;
    hse?: HseSummary;
  } & CarbonBranchScalars;

/** By-branch root = monthly operational merge + org-wide prior ESG + branches. */
export type EsgByBranchRootMerged = EsgPriorMonthScalars & {
  persona: EsgDashboardPersona;
  organization_id: string;
  month_date: string;
  branches: EsgBranchMonthlyRow[];
  unassigned_waste: EsgBranchMonthlyRow | null;
  training_hours_total: number | null;
  hse?: HseSummary;
  previous_month_hse?: HseSummary;
};

export type EsgDashboardByBranchResponse = DashboardStatistics & EsgByBranchRootMerged;

export type EsgMonthQuery =
  | { month_date: string }
  | { year: number; month: number };

/** Keys on merged monthly ESG JSON that are not inherited `DashboardStatistics` fields. */
export const ESG_MONTHLY_NON_OPERATIONAL_KEYS = new Set<string>([
  'persona',
  'organization_id',
  'month_date',
  'water_consumption',
  'water_amount_sar',
  'electricity_consumption_kwh',
  'electricity_amount_sar',
  'oil_consumption_liters',
  'oil_amount_sar',
  'gas_consumption_m3',
  'gas_amount_sar',
  'total_waste_diverted_kg',
  'waste_diversion_rate_percent',
  'total_waste_recycled_kg',
  'recycled_waste_breakdown_by_type',
  'fill_level_report_count',
  'training_hours_total',
  'previous_month_water_consumption',
  'previous_month_water_amount_sar',
  'previous_month_electricity_consumption_kwh',
  'previous_month_electricity_amount_sar',
  'previous_month_oil_consumption_liters',
  'previous_month_oil_amount_sar',
  'previous_month_gas_consumption_m3',
  'previous_month_gas_amount_sar',
  'previous_month_fill_level_report_count',
  'previous_month_training_hours_total',
  'total_avoided_tco2e',
  'total_energy_saved_kwh',
  'avoided_tco2e_by_stream',
  'avoided_tco2e_by_material',
  'calculation_run_count',
  'calculation_skipped_count',
  'calculation_failed_count',
  'calculation_pending_count',
  'calculation_missing_count',
  'calculation_warnings',
  'hse',
  'previous_month_hse',
]);

/** Extra keys on by-branch root beyond `DashboardStatistics`. */
export const ESG_BY_BRANCH_ROOT_EXTRA_KEYS = new Set<string>([
  ...ESG_MONTHLY_NON_OPERATIONAL_KEYS,
  'branches',
  'unassigned_waste',
]);
